"use client";

import { MobileSidebarToggle } from "@/components/mobile-sidebar-toggle";
import { useCurrentOrganization } from "@/hooks/spacetime";
import { useOrgTransitionHeld } from "@/lib/org-transition";
import { usePathname, useRouter } from "next/navigation";
import { useEffect } from "react";

type SettingsLayoutProps = {
  children: React.ReactNode;
};

export default function SettingsLayout({ children }: SettingsLayoutProps) {
  const pathname = usePathname();
  const router = useRouter();
  const { organization, isReady } = useCurrentOrganization();
  // useCurrentOrganization only resolves to `null` once ready when the
  // caller belongs to zero organizations (resolveCurrentOrganization always
  // returns one of `organizations` when that list is non-empty) — so this is
  // exactly the legacy `user.organizations.length === 0` check, without
  // needing a separate useCurrentUser() call. Optimistic while `!isReady`,
  // matching legacy's un-gated initial render.
  // A teardown still on screen (the delete dialog's confirmation and exit)
  // holds this off: the org row is already gone by then, and unmounting the
  // subtree here would rip that dialog out mid-transition.
  const isOrgTransitionHeld = useOrgTransitionHeld();
  const lacksActiveOrganization =
    isReady && !organization && !isOrgTransitionHeld;
  const allowedWithoutOrganization =
    pathname === "/settings/profile" ||
    pathname === "/settings/organizations" ||
    pathname === "/settings/tax-applications" ||
    pathname === "/settings/grace-periods" ||
    pathname === "/settings";

  useEffect(() => {
    if (lacksActiveOrganization && !allowedWithoutOrganization) {
      router.replace("/settings/organizations");
    }
  }, [allowedWithoutOrganization, lacksActiveOrganization, router]);

  if (lacksActiveOrganization && !allowedWithoutOrganization) {
    return null;
  }

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden bg-background">
      <MobileSidebarToggle layoutName="Settings" />
      <div className="lb-scrollbar-overlay min-h-0 min-w-0 flex-1 overflow-x-hidden overscroll-y-auto bg-background">
        <div
          className={`mx-auto mb-8 flex w-full max-w-5xl flex-col pt-3.5 sm:mb-20 sm:pt-5 md:pt-7 ${pathname.includes("subscriptions") || pathname.includes("api-keys") || pathname.includes("tax-applications") || pathname.includes("grace-periods") ? "" : "items-center"} px-3.5 sm:px-5`}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
